import React, { useCallback, useEffect, useRef } from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import axios from "src/config/axios";
import { Form, Formik, useFormikContext } from "formik";
import MultiLanguageInputText from "src/components/Forms/MultiLanguageInputText";
import FormCheckbox from "src/components/Forms/FormCheckbox";
import FormInputText from "src/components/Forms/FormInputText";
import FormSelectBox from "src/components/Forms/FormSelectBox";
import FormTranslatedEditor from "src/components/Forms/FormTranslatedEditor";

const triggerOptions = [
  { name: "Nach Checkout", value: "after_checkout" },
  { name: "Reservierungsanfrage", value: "reservation_requested" },
  // { name: "Reservierungsbestätigung", value: "reservation_accepted" },
];

const placeholders = [
  "{{business_name}}",
  "{{contact_name}}",
  "{{contact_first_name}}",
  "{{contact_last_name}}",
  "{{stay_from}}",
  "{{stay_to}}",
  "{{checkout_at}}",
];

const templateContentFields = [
  "subject",
  "subject_it",
  "subject_en",
  "body",
  "body_it",
  "body_en",
];

const EmailTemplateForm = ({ emailTemplate }) => {
  const { values, setFieldValue } = useFormikContext();
  const initialTriggerHandled = useRef(false);

  const applyTemplate = useCallback(
    (trigger) => {
      axios
        .get("/api/v1/email_templates/template.json", { params: { trigger } })
        .then(function (response) {
          templateContentFields.forEach((fieldName) => {
            setFieldValue(fieldName, response.data[fieldName] || "");
          });
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    [setFieldValue]
  );

  useEffect(() => {
    if (!values.trigger) {
      return;
    }

    if (!initialTriggerHandled.current) {
      initialTriggerHandled.current = true;

      if (!emailTemplate.id) {
        applyTemplate(values.trigger);
      }

      return;
    }

    if (values.trigger) {
      applyTemplate(values.trigger);
    }
  }, [applyTemplate, emailTemplate.id, values.trigger]);

  const handleTriggerChange = (e) => {
    const trigger = e.target.value;

    setFieldValue("trigger", trigger);
  };

  let bodyLabel = "Inhalt";
  let hasSubject = true;

  if (
    values.trigger == "reservation_requested" ||
    values.trigger == "reservation_accepted"
  ) {
    hasSubject = false;
    bodyLabel = "Info-Text am Ende";
  }

  return (
    <Form autoComplete="false">
      <FormInputText name="name" label="Name" required />
      <FormSelectBox
        name="trigger"
        label="Trigger"
        options={triggerOptions}
        onChange={handleTriggerChange}
        required
      />
      <FormCheckbox name="active" label="Aktiv" className="mb-3" />

      {hasSubject && (
        <MultiLanguageInputText
          name="subject"
          label="Betreff"
          languages={["de", "it", "en"]}
          required
        />
      )}

      <Alert variant="info">Platzhalter: {placeholders.join(", ")}</Alert>
      <FormTranslatedEditor
        name="body"
        label={bodyLabel}
        languages={["de", "it", "en"]}
        required
      />
    </Form>
  );
};

const EditEmailTemplateDialog = ({
  toggle,
  emailTemplate,
  handleSuccess = null,
}) => {
  function validate(values) {
    const errors = {};

    if (!values.name || values.name.trim() === "") {
      errors.name = "Name ist ein Pflichtfeld";
    }

    if (
      values.trigger != "reservation_requested" &&
      values.trigger != "reservation_accepted"
    ) {
      if (!values.subject && !values.subject_it && !values.subject_en) {
        errors.subject =
          "Es muss für mindestens eine Sprache ein Betreff gesetzt sein.";
        errors.subject_it =
          "Es muss für mindestens eine Sprache ein Betreff gesetzt sein.";
        errors.subject_en =
          "Es muss für mindestens eine Sprache ein Betreff gesetzt sein.";
      }
    }

    if (!values.body && !values.body_it && !values.body_en) {
      errors.body =
        "Es muss für mindestens eine Sprache ein Inhalt gesetzt sein.";
      errors.body_it =
        "Es muss für mindestens eine Sprache ein Inhalt gesetzt sein.";
      errors.body_en =
        "Es muss für mindestens eine Sprache ein Inhalt gesetzt sein.";
    }

    return errors;
  }

  function submit(values, { setSubmitting, setErrors }) {
    setSubmitting(true);
    const request = emailTemplate.id
      ? axios.put(`/api/v1/email_templates/${emailTemplate.id}.json`, {
          email_template: values,
        })
      : axios.post("/api/v1/email_templates.json", {
          email_template: values,
        });
    request
      .then(function () {
        toggle();

        if (handleSuccess) {
          handleSuccess();
        }
      })
      .catch(function (e) {
        setErrors(e.response.data);
      })
      .finally(function () {
        setSubmitting(false);
      });
  }

  const formRef = useRef();

  const handleSubmit = () => {
    formRef.current.handleSubmit();
  };

  return (
    <Modal show={true} onHide={toggle} size="lg">
      <Modal.Header>
        <Modal.Title>
          {!emailTemplate.id
            ? "E-Mail-Template erstellen"
            : "E-Mail-Template bearbeiten"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          innerRef={formRef}
          initialValues={emailTemplate}
          validate={validate}
          onSubmit={submit}
        >
          {() => <EmailTemplateForm emailTemplate={emailTemplate} />}
        </Formik>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={toggle}>
          Schließen
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Speichern
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditEmailTemplateDialog;
